import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DismissIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ManagetNav from '../../components/ManagerNav/ManagerNav';
import ManagerAlertApi, { ManagerAlertDto } from '../../api/ManagerAlertApi';
import CaseRemindersPanel from '../../components/CaseRemindersPanel/CaseRemindersPanel';
import './ManagerAlertsView.scss';

interface SearchFilters {
  ManagerAlert_Title: string;
  ManagerAlert_Desc: string;
  ManagerAlert_CreatedAt_From: string;
  ManagerAlert_CreatedAt_To: string;
  ManagerAlert_Active: string; // 'both', 'active', 'inactive'
}

const initialFilters: SearchFilters = {
  ManagerAlert_Title: '',
  ManagerAlert_Desc: '',
  ManagerAlert_CreatedAt_From: '',
  ManagerAlert_CreatedAt_To: '',
  ManagerAlert_Active: 'both'
};

const ManagerAlertsView: React.FC = () => {
  const navigate = useNavigate();
  
  const [alerts, setAlerts] = useState<ManagerAlertDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentFrom, setCurrentFrom] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);

  const RESULTS_PER_PAGE = 20;

  const fetchAlerts = useCallback(async (searchFilters = filters, from = 0, appendResults = false) => {
    if (!appendResults) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const to = from + RESULTS_PER_PAGE;
      const apiFilters: any = {
        from,
        to
      };
      
      // Add filters if they have values
      if (searchFilters.ManagerAlert_Title.trim()) {
        apiFilters.ManagerAlert_Title = searchFilters.ManagerAlert_Title.trim();
      }
      if (searchFilters.ManagerAlert_Desc.trim()) {
        apiFilters.ManagerAlert_Desc = searchFilters.ManagerAlert_Desc.trim();
      }
      if (searchFilters.ManagerAlert_CreatedAt_From) {
        apiFilters.ManagerAlert_CreatedAt_From = searchFilters.ManagerAlert_CreatedAt_From;
      }
      if (searchFilters.ManagerAlert_CreatedAt_To) {
        apiFilters.ManagerAlert_CreatedAt_To = searchFilters.ManagerAlert_CreatedAt_To;
      }
      if (searchFilters.ManagerAlert_Active === 'active') {
        apiFilters.ManagerAlert_Active = true;
      } else if (searchFilters.ManagerAlert_Active === 'inactive') {
        apiFilters.ManagerAlert_Active = false;
      }
      // If 'both', don't add ManagerAlert_Active parameter

      const data = await ManagerAlertApi.getManagerAlerts(apiFilters);
      
      if (appendResults) {
        setAlerts(prevAlerts => [...prevAlerts, ...data]);
      } else {
        setAlerts(data);
      }
      
      // Check if there are more results (if we got exactly RESULTS_PER_PAGE, there might be more)
      setHasMoreData(data.length === RESULTS_PER_PAGE);
      setCurrentFrom(from);
      setHasSearched(true);
      
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [RESULTS_PER_PAGE]);

  useEffect(() => {
    fetchAlerts(initialFilters, 0, false);
  }, [fetchAlerts]);

  const handleFilterChange = (field: keyof SearchFilters, value: string | boolean) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleSearch = () => {
    setCurrentFrom(0);
    setHasMoreData(false);
    fetchAlerts(filters, 0, false);
  };

  const handleLoadMore = () => {
    const nextFrom = currentFrom + RESULTS_PER_PAGE;
    fetchAlerts(filters, nextFrom, true);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentFrom(0);
    setHasMoreData(false);
    setError(null);
    fetchAlerts(initialFilters, 0, false);
  };

  const handleDismissAlert = async (alertGuid: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    try {
      await ManagerAlertApi.dismissAlert(alertGuid);
      
      // Update local state instead of refetching
      setAlerts(prevAlerts => 
        prevAlerts.map(alert => 
          alert.ManagerAlert_guid === alertGuid 
            ? { ...alert, ManagerAlert_Active: false }
            : alert
        ).filter(alert => {
          // If we're filtering for active alerts only, remove dismissed alerts from view
          if (filters.ManagerAlert_Active === 'active') {
            return alert.ManagerAlert_Active;
          }
          // Otherwise keep all alerts (both active and inactive)
          return true;
        })
      );
      
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
      setError(err instanceof Error ? err.message : 'Failed to dismiss alert');
    }
  };

  const handleOpenAlert = async (alert: ManagerAlertDto, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (alert.ManagerAlert_Url) {
      navigate(alert.ManagerAlert_Url);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <ManagetNav />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manager Alerts
          </Typography>
          
        </Box>

        {/* Outstanding work, above the dismissible alerts — see CaseRemindersPanel. */}
        <CaseRemindersPanel />

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Alerts
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="Alert Title"
              value={filters.ManagerAlert_Title}
              onChange={(e) => handleFilterChange('ManagerAlert_Title', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
            />
            
            <TextField
              label="Alert Description"
              value={filters.ManagerAlert_Desc}
              onChange={(e) => handleFilterChange('ManagerAlert_Desc', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 250, flex: 1 }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="Date From"
              type="date"
              value={filters.ManagerAlert_CreatedAt_From}
              onChange={(e) => handleFilterChange('ManagerAlert_CreatedAt_From', e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            
            <TextField
              label="Date To"
              type="date"
              value={filters.ManagerAlert_CreatedAt_To}
              onChange={(e) => handleFilterChange('ManagerAlert_CreatedAt_To', e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.ManagerAlert_Active}
                onChange={(e) => handleFilterChange('ManagerAlert_Active', e.target.value)}
                label="Status"
              >
                <MenuItem value="both">Any</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              disabled={loading}
            >
              Search Alerts
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {hasSearched && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" color="textSecondary">
                    {alerts.length === 0 ? 'No alerts found' : `${alerts.length} alert${alerts.length === 1 ? '' : 's'} found`}
                  </Typography>
                </Box>
              )}
              
              {alerts.length === 0 && hasSearched ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No alerts found matching your search criteria.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {alerts.map((alert) => (
                          <TableRow
                            key={alert.ManagerAlert_guid}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              {alert.ManagerAlert_Title}
                            </TableCell>
                            <TableCell sx={{ 
                              maxWidth: '300px',
                              wordWrap: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {alert.ManagerAlert_Desc.length > 100 
                                ? `${alert.ManagerAlert_Desc.substring(0, 100)}...`
                                : alert.ManagerAlert_Desc
                              }
                            </TableCell>
                            <TableCell>{formatDateTime(alert.ManagerAlert_CreatedAt)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={alert.ManagerAlert_Active ? 'Active' : 'Dismissed'}
                                color={alert.ManagerAlert_Active ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {alert.ManagerAlert_Url && (
                                  <Tooltip title="Open Alert URL">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleOpenAlert(alert, e)}
                                      color="primary"
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                
                                {alert.ManagerAlert_Active && (
                                  <Tooltip title="Dismiss Alert">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleDismissAlert(alert.ManagerAlert_guid, e)}
                                      color="error"
                                    >
                                      <DismissIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Load More Button */}
                  {hasMoreData && (
                    <Box sx={{ p: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
                      >
                        {loadingMore ? 'Loading...' : `Load More (Showing ${alerts.length} results)`}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ManagerAlertsView;